import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Upload } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RICH_FIELDS = new Set(['body', 'left_body']);

const BLOCK_TEMPLATES = {
    hero: { type: 'hero', props: { heading: 'Your headline', subheading: 'Your subheading', image_url: '', cta_label: '', cta_href: '' } },
    text: { type: 'text', props: { heading: '', body: '', align: 'left' } },
    image: { type: 'image', props: { image_url: '', caption: '', href: '' } },
    cta: { type: 'cta', props: { heading: '', body: '', button_label: 'Learn more', button_href: '/' } },
    quote: { type: 'quote', props: { quote: '', author: '' } },
    two_column: { type: 'two_column', props: { left_heading: '', left_body: '', right_image_url: '', flip: false } },
    // 'featured_pillar' — pillar_id = '' means "use the currently featured pillar (rotation/override)".
    // A specific id pins the block to that pillar regardless of site-wide feature settings.
    featured_pillar: { type: 'featured_pillar', props: { heading: "Meet our pillar", pillar_id: '' } }
};

export default function HomeBlocksPanel() {
    const [blocks, setBlocks] = useState([]);
    const [pillars, setPillars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/home-blocks`),
            axios.get(`${API_URL}/api/pillars`)
        ])
            .then(([b, p]) => {
                setBlocks(b.data.blocks || []);
                setPillars(p.data || []);
            })
            .catch(() => toast.error('Failed to load blocks'))
            .finally(() => setLoading(false));
    }, []);

    const addBlock = (type) => {
        const tpl = JSON.parse(JSON.stringify(BLOCK_TEMPLATES[type]));
        tpl.id = `tmp_${Date.now()}`;
        setBlocks([...blocks, tpl]);
    };

    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= blocks.length) return;
        const next = [...blocks];
        [next[idx], next[j]] = [next[j], next[idx]];
        setBlocks(next);
    };

    const remove = (idx) => {
        if (!window.confirm('Remove this block?')) return;
        setBlocks(blocks.filter((_, i) => i !== idx));
    };

    const updateProp = (idx, key, val) => {
        const next = [...blocks];
        next[idx] = { ...next[idx], props: { ...next[idx].props, [key]: val } };
        setBlocks(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(`${API_URL}/api/home-blocks`, { blocks }, { withCredentials: true });
            setBlocks(data.blocks || []);
            window.dispatchEvent(new CustomEvent('home-blocks-changed'));
            toast.success('Homepage saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" /></div>;

    return (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 space-y-6" data-testid="home-blocks-panel">
            <div>
                <p className="overline mb-2">Content</p>
                <h2 className="font-serif text-3xl text-[#1A1A2E]">Homepage Builder</h2>
                <p className="text-sm text-[#4A4A68] mt-2">Add and reorder blocks that appear on your landing page. Leave empty to use the default hero + about + memberships.</p>
            </div>

            <div className="flex flex-wrap gap-2 border-b border-[#E5E0D8] pb-4">
                {Object.keys(BLOCK_TEMPLATES).map(t => (
                    <button key={t} onClick={() => addBlock(t)} className="btn-secondary text-xs flex items-center gap-1" data-testid={`add-block-${t}`}>
                        <Plus className="w-3 h-3" /> {t.replace('_', ' ')}
                    </button>
                ))}
            </div>

            {blocks.length === 0 && (
                <p className="text-center py-8 text-[#4A4A68] italic">No blocks yet. Click a block type above to add one.</p>
            )}

            {blocks.map((b, i) => (
                <div key={b.id || i} className="border border-[#E5E0D8] p-4 rounded-sm space-y-3" data-testid={`block-${i}`}>
                    <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-widest text-[#5B2D8E] font-medium">{b.type.replace('_', ' ')}</span>
                        <div className="flex gap-1">
                            <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-[#F2EBE5] rounded disabled:opacity-30" data-testid={`up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                            <button onClick={() => move(i, 1)} disabled={i === blocks.length - 1} className="p-1 hover:bg-[#F2EBE5] rounded disabled:opacity-30" data-testid={`down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                            <button onClick={() => remove(i)} className="p-1 hover:bg-red-50 rounded" data-testid={`remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                        </div>
                    </div>
                    {Object.entries(b.props).map(([k, v]) => (
                        <div key={k}>
                            <label className="block text-xs uppercase tracking-widest text-[#4A4A68] mb-1">{k.replace(/_/g, ' ')}</label>
                            {typeof v === 'boolean' ? (
                                <input type="checkbox" checked={v} onChange={(e) => updateProp(i, k, e.target.checked)} data-testid={`block-${i}-${k}`} />
                            ) : RICH_FIELDS.has(k) ? (
                                <RichTextEditor
                                    value={v || ''}
                                    onChange={(html) => updateProp(i, k, html)}
                                    minHeight="140px"
                                    variant="compact"
                                />
                            ) : k.endsWith('subheading') ? (
                                <textarea rows={3} value={v || ''} onChange={(e) => updateProp(i, k, e.target.value)} className="w-full resize-none text-sm" data-testid={`block-${i}-${k}`} />
                            ) : k.endsWith('image_url') ? (
                                <div className="flex gap-2 items-center">
                                    <input type="text" value={v || ''} onChange={(e) => updateProp(i, k, e.target.value)} className="flex-1 text-sm" placeholder="https://… or upload" data-testid={`block-${i}-${k}`} />
                                    <label className="btn-secondary text-xs flex items-center gap-1 cursor-pointer">
                                        <Upload className="w-3 h-3" /> Upload
                                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                            const file = e.target.files?.[0]; if (!file) return;
                                            const fd = new FormData(); fd.append('file', file);
                                            try {
                                                const { data } = await axios.post(`${API_URL}/api/upload/image`, fd, { withCredentials: true });
                                                updateProp(i, k, `${API_URL}${data.url}`);
                                                toast.success('Image uploaded');
                                            } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                                        }} />
                                    </label>
                                </div>
                            ) : k === 'pillar_id' ? (
                                <select
                                    value={v || ''}
                                    onChange={(e) => updateProp(i, k, e.target.value)}
                                    className="w-full text-sm"
                                    data-testid={`block-${i}-${k}`}
                                >
                                    <option value="">— Auto (use currently featured pillar) —</option>
                                    {pillars.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.name}{p.specialty ? ` · ${p.specialty}` : ''}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input type="text" value={v || ''} onChange={(e) => updateProp(i, k, e.target.value)} className="w-full text-sm" data-testid={`block-${i}-${k}`} />
                            )}
                        </div>
                    ))}
                </div>
            ))}

            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-home-blocks-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save homepage
            </button>
        </div>
    );
}
